/** @format */

export type SearchParamPatch = Record<string, string | number | boolean | null | undefined>

interface RouterHistory {
  replace: (path: string) => void
}

interface RouterLocation {
  pathname: string
  search: string
}

/** 基于当前 search 合并 query 参数，返回新的 search 字符串（不含 `?`）。 */
export function patchSearchParams(currentSearch: string, patch: SearchParamPatch): string {
  const searchParams = new URLSearchParams(currentSearch)
  Object.entries(patch).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      searchParams.delete(key)
    } else {
      searchParams.set(key, String(value))
    }
  })
  return searchParams.toString()
}

/** 从 location.search 解析 TableSearch 同步的 JSON 参数。 */
export function parseTableSearchParams<T = Record<string, unknown>>(
  search: string,
  paramsKey = 'tableSearch'
): T | undefined {
  const searchParams = new URLSearchParams(search)
  const paramsStr = searchParams.get(paramsKey)
  return paramsStr ? JSON.parse(paramsStr) : undefined
}

/** 拼接 pathname 与 search（search 可带或不带 `?`）。 */
export function buildLocationUrl(pathname: string, search = ''): string {
  const normalizedSearch = search.startsWith('?') ? search.slice(1) : search
  return normalizedSearch ? `${pathname}?${normalizedSearch}` : pathname
}

export function getLocationUrl(location: RouterLocation): string {
  return buildLocationUrl(location.pathname, location.search)
}

/** 保留当前路径与其它 query，仅更新指定参数并 replace 当前 URL（不产生历史记录）。 */
export function replaceSearchParams(
  history: RouterHistory,
  location: RouterLocation,
  patch: SearchParamPatch
): void {
  const nextSearch = patchSearchParams(location.search, patch)
  const nextUrl = buildLocationUrl(location.pathname, nextSearch)
  if (nextUrl === getLocationUrl(location)) {
    return
  }
  history.replace(nextUrl)
}

/** 将 tableSearch 等 JSON 参数写入 URL，仅在内容变化时 replace。 */
export function replaceTableSearchParams(
  history: RouterHistory,
  location: RouterLocation,
  params: Record<string, unknown>,
  paramsKey = 'tableSearch'
): void {
  const paramsStr = JSON.stringify(params)
  const searchParams = new URLSearchParams(location.search)
  if (searchParams.get(paramsKey) === paramsStr) {
    return
  }
  searchParams.set(paramsKey, paramsStr)
  const nextUrl = buildLocationUrl(location.pathname, searchParams.toString())
  if (nextUrl === getLocationUrl(location)) {
    return
  }
  history.replace(nextUrl)
}
